import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IoSearchOutline, IoCloseCircle } from 'react-icons/io5';
import { Fonts, Palette } from '../../components/utils';

//Shared search field for chat.
const ChatSearchField = ({
  value,
  onChange,
  onClear,
  placeholder,
  ariaLabel,
  disabled = false,
  variant = 'outlined'
}) => {
  const { t } = useTranslation();
  const filled = variant === 'filled';
  const iconSize = filled ? 18 : 16;

  return (
    <Wrap $filled={filled}>
      <Icon aria-hidden="true" $filled={filled}>
        <IoSearchOutline size={iconSize} />
      </Icon>
      <Input
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        disabled={disabled}
      />
      {onClear && value ? (
        <ClearButton type="button" aria-label={t('Clear')} onClick={onClear}>
          <IoCloseCircle size={18} />
        </ClearButton>
      ) : null}
    </Wrap>
  );
};

const Wrap = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
  border-radius: 8px;

  ${({ $filled }) =>
    $filled
      ? `
    gap: 10px;
    min-height: 56px;
    height: 56px;
    padding: 0 16px;
    border: none;
    background: ${Palette.WHITE_SMOKE};
  `
      : `
    gap: 8px;
    height: 48px;
    padding: 12px 16px;
    border: 1px solid #d5d5d5;
    background: ${Palette.WHITE};
  `}
`;

const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $filled }) => ($filled ? '18px' : '16px')};
  height: ${({ $filled }) => ($filled ? '18px' : '16px')};
  color: ${Palette.CONTENT_TERTIARY};
  flex-shrink: 0;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 24px;
  border: none;
  outline: none;
  background: transparent;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  padding: 0;
  appearance: none;

  &:disabled {
    cursor: default;
  }

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    -webkit-appearance: none;
    display: none;
  }

  &::placeholder {
    color: ${Palette.CONTENT_TERTIARY};
    opacity: 1;
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${Palette.CONTENT_TERTIARY};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${Palette.BLACK};
  }

  &:focus {
    outline: none;
  }
`;

export default ChatSearchField;
